import React, { useEffect } from 'react';
import { QRCode } from 'react-qr-code';
import { brightnessControl } from '../../utils/brightnessControl';
import { displayNameFor, formatMonth } from './helpers';
import UserAvatar from '../../components/UserAvatar';
import { TIER_NAMES } from '../../constants/techTiers';

// The membership card, as its own object rather than a dialog with a card in it.
//
// This is deliberately not a DSSheet. A sheet is chrome around content you are
// reading or a form you are filling in; this is a physical-feeling card the
// member holds up, so it is a light card on a dark scrim with its own purple
// band, its own overhanging avatar, and no title bar or footer. There is no
// Close button either: the nav button that opened it turns into an X and stays
// under the thumb, which is where the hand already is.
//
// The QR is rendered directly rather than through EnhancedQRCode, whose framing
// (its own white panel, name caption, and Fullscreen button) would be a second
// card inside this one. The wake lock EnhancedQRCode provides is the part worth
// keeping, and it is kept here: the screen has to stay lit while someone is
// scanning it at the door.
function MembershipCardSheet({ currentUser, membershipSettings, isMember, hasApplication, onClose }) {
  useEffect(() => {
    brightnessControl.instance.requestWakeLock();
    return () => {
      brightnessControl.instance.releaseWakeLock();
    };
  }, []);

  // Escape closes it, same as every other overlay on the screen.
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const tier = currentUser.tech_tier ?? 0;
  const memberSince = formatMonth(membershipSettings?.start_date);

  const statusLine = isMember
    ? (memberSince ? `Member since ${memberSince}` : 'Full member')
    : hasApplication ? 'Application in progress' : 'Guest';

  return (
    <div
      className="mm-cardsheet"
      role="dialog"
      aria-modal="true"
      aria-label={isMember ? 'Membership card' : 'Guest card'}
    >
      <div className="mm-cardsheet__scrim" onClick={onClose} />

      <div className="mm-cardsheet__dock">
        <div className="mm-cardsheet__card">
          <div className="mm-cardsheet__band">
            <div className="mm-cardsheet__qr">
              {/* Level H so it still scans with a thumb over a corner. Explicit
                  colours rather than inherited ones: a QR that picks up the
                  page's text colour is a QR that stops scanning. */}
              <QRCode
                value={`${window.location.origin}/verify/${currentUser.qr_code}`}
                size={160}
                level="H"
                bgColor="#FFFFFF"
                fgColor="#0F172A"
                style={{ width: '100%', height: 'auto' }}
              />
            </div>
          </div>

          {/* Straddles the seam between the band and the body, which is what makes
              the two halves read as one card rather than two stacked blocks. */}
          <div className="mm-cardsheet__avatar-row">
            <UserAvatar person={currentUser} className="mm-cardsheet__avatar" />
          </div>

          <div className="mm-cardsheet__body">
            <p className="mm-cardsheet__name">
              {displayNameFor(currentUser)}
              {currentUser.pronouns && (
                <span className="mm-cardsheet__pronouns">{currentUser.pronouns}</span>
              )}
            </p>

            <div className="mm-cardsheet__chips">
              {isMember && <span className="mm-cardchip mm-cardchip--member">Full member</span>}
              {!isMember && hasApplication && <span className="mm-cardchip mm-cardchip--applicant">Applicant</span>}
              {!isMember && !hasApplication && <span className="mm-cardchip mm-cardchip--guest">Guest</span>}
              {isMember && tier > 0 && (
                <span className="mm-cardchip mm-cardchip--tier">Tier {tier} · {TIER_NAMES[tier]}</span>
              )}
            </div>

            <p className="mm-cardsheet__status">{statusLine}</p>
            <p className="mm-cardsheet__hint">
              Hold this under the reader on the machine, or show it at the door.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MembershipCardSheet;
